import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import IconButton from "../../shared/v2/IconButton/IconButton";
import HoverButton from "./HoverButton";
import Menu from "./Menu";
import PanelIcon, { PanelIconCaret } from "./PanelIcon";
import useMobileMenu from "./hooks/useMobileMenu";
import MobileSidebar from "./MobileSidebar";
import ICON_MAP from "./helpers/icons";
import ACTION_MAP from "./ActionModals";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";
import { setIsSidebarOpen, setSelectedMenuOption, toggleMobileMenu } from "../../reducers/layoutReducer";

const Sidebar = () => {
  const {
    data: { isMobile },
  } = useMobileMenu();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const sidebar = useSelector(({ layout }) => layout.sidebar);
  const selectedMenuOption = useSelector(({ layout }) => layout.selectedMenuOption);
  const theme = useSelector(({ layout }) => layout.theme);
  const mounted = useRef(false);
  const [isOpen, setIsOpen] = useState(localStorage.getItem("nav:closedManually") !== "true");
  const [actionIsOpen, setActionIsOpen] = useState(false);

  useEffect(() => {
    dispatch(setIsSidebarOpen(isOpen));
  }, [isOpen]);

  const preventMenuOpen = [
    "Add-Ons",
    "Dashboard",
    "Messages",
    "Projects",
    "Calendar",
    "Marketer",
    "Phone System",
    "Recruiter.ai",
    "Opportunities",
  ].includes(selectedMenuOption?.name);
  const Action = ACTION_MAP[selectedMenuOption?.name];

  useEffect(() => {
    if (!Action) setActionIsOpen(false);
  }, [Action]);

  useEffect(() => {
    if (!selectedMenuOption) return;
    const menuItem = sidebar.menu.find((m) => m.name === selectedMenuOption.name);
    if (menuItem) dispatch(setSelectedMenuOption(menuItem));
  }, [sidebar, selectedMenuOption?.name]);

  useEffect(() => {
    let newSelection = "Settings";
    const { pathname, search } = window.location;
    const fullPath = `${pathname}${search}`;
    if (/^\/dashboard/.test(fullPath)) newSelection = "Dashboard";
    else if (/^\/messages/.test(fullPath)) newSelection = "Messages";
    else if (/^\/phone_system/.test(fullPath)) newSelection = "Phone System";
    else if (
      /^\/people\/(?:leads|brivity_valuations|brivity_leads|brivity_idx|kwkly|zillow|all_people.+selected_lead_ponds|.+from_leads=1)/.test(
        fullPath,
      )
    )
      newSelection = "Leads";
    else if (new RegExp(`^/people/(?!${currentUser.person.slug})`).test(fullPath)) newSelection = "People";
    else if (/^\/(?:search_listings|partner_searches)/.test(fullPath)) newSelection = "Search";
    else if (/^\/(?:listings|transactions)/.test(fullPath)) newSelection = "Transactions";
    else if (/^\/projects/.test(fullPath)) newSelection = "Projects";
    else if (/^\/agent_stats/.test(fullPath)) newSelection = "Reporting";
    else if (/^\/marketing/.test(fullPath)) newSelection = "Marketing";
    else if (/^\/events/.test(fullPath)) newSelection = "Calendar";
    else if (/^\/tasks/.test(fullPath)) newSelection = "Tasks";
    else if (/^\/opportunities/.test(fullPath)) newSelection = "Opportunities";
    else if (/^\/website/.test(fullPath)) newSelection = "Website";
    else if (/^\/add_ons$/.test(fullPath)) newSelection = "Add-Ons";

    const menuItem = sidebar.menu.find((m) => m.name === newSelection);
    if (menuItem) dispatch(setSelectedMenuOption(menuItem));
  }, []);

  useEffect(() => {
    if (preventMenuOpen) {
      setIsOpen(false);
    } else if (mounted.current) {
      setIsOpen(true);
    }
    if (selectedMenuOption?.name && !mounted.current) mounted.current = true;
  }, [selectedMenuOption?.name, preventMenuOpen]);

  return (
    <>
      {isMobile && (
        <MobileSidebar
          onAction={
            Action
              ? () => {
                  setActionIsOpen(true);
                  dispatch(toggleMobileMenu(false));
                }
              : null
          }
          preventMenuOpen={preventMenuOpen}
        />
      )}
      {!isMobile && (
        <nav
          className={`tw-transition-transform tw-flex tw-max-w-[260px] tw-h-full ${
            theme.name === "place-theme" ? "tw-bg-[#121212]" : "tw-bg-[#f9fbfb]"
          }`}
          data-cy="side-navbar"
          id="sidebar"
        >
          <div className="tw-relative tw-flex tw-flex-col tw-gap-[4px] tw-w-[52px] tw-h-full">
            <div className="nav-scrollbar tw-z-2 tw-flex tw-flex-col tw-gap-[6px] tw-h-full tw-overflow-x-hidden tw-overflow-y-auto">
              {sidebar.menu.map((menuItem) => {
                const { enabled, name, href, target } = menuItem;
                if (!enabled) return null;
                const Icon = ICON_MAP[name];
                return (
                  <HoverButton
                    key={name}
                    id={`${name.toLowerCase()}-nav-menu`}
                    tooltip={name}
                    isSelected={name === selectedMenuOption?.name}
                    href={href}
                    target={target}
                    onClick={() => {
                      if (name === selectedMenuOption?.name && !preventMenuOpen) setIsOpen((o) => !o);
                      dispatch(setSelectedMenuOption(menuItem));
                    }}
                  >
                    <Icon size="l" />
                  </HoverButton>
                );
              })}
            </div>

            <div className="tw-flex-0 tw-flex tw-justify-center tw-pl-[8px] tw-pb-[16px]">
              <IconButton
                size="custom"
                className="tw-relative tw-w-[32px] tw-h-[32px] tw-p-[4px] !tw-rounded-[4px] tw-border-0 hover:tw-bg-gray/10 disabled:tw-hidden active:tw-bg-gray/20"
                schema="transparent"
                disabled={preventMenuOpen}
                onClick={() =>
                  setIsOpen((o) => {
                    const open = !o;
                    window.localStorage.setItem("nav:closedManually", !open);
                    return open;
                  })
                }
              >
                <PanelIcon />
                <PanelIconCaret className={isOpen ? "tw-rotate-180" : ""} />
              </IconButton>
            </div>
          </div>

          <div
            className={`tw-transition-all tw-ease-in-out tw-duration-300 tw-mr-[8px] tw-overflow-x-hidden ${
              theme.name === "place-theme" ? "tw-text-white" : ""
            } ${isOpen ? "tw-w-[200px]" : "tw-w-0 tw-opacity-0"}`}
          >
            <Menu
              title={selectedMenuOption?.name}
              options={selectedMenuOption?.options?.filter(
                (o) => typeof o.enabled === "undefined" || o.enabled,
              )}
              onAction={Action ? () => setActionIsOpen(true) : null}
            />
          </div>
        </nav>
      )}
      {Action ? <Action show={actionIsOpen} onClose={() => setActionIsOpen(false)} /> : null}
    </>
  );
};

export default Sidebar;
